import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Typography } from "@/components/ui/Typography";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { isValidEmail } from "@/lib/validation";

interface InviteInfo {
  propertyName: string;
  unitAlias: string;
  unitNumber: string;
  maxTenants: number;
  agreementEndDate: string | null;
  moveInDate: string | null;
  expiresAt: string;
  alreadySubmitted: boolean;
}

const emptyTenant = () => ({ name: "", phone: "", email: "" });

export function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<{ name: string; phone: string; email: string }[]>([emptyTenant()]);
  const [submitting, setSubmitting] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid link");
      setLoading(false);
      return;
    }
    api
      .get<InviteInfo>(`/join/${token}`)
      .then((info) => {
        setInvite(info);
        setAlreadySubmitted(info.alreadySubmitted);
        setTenants(
          Array.from({ length: Math.max(1, info.maxTenants) }, (_, i) =>
            i === 0 ? emptyTenant() : { name: "", phone: "", email: "" }
          )
        );
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : "This link is invalid or has expired.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  function updateTenant(index: number, field: "name" | "phone" | "email", value: string) {
    setTenants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }

  const validTenants = tenants.filter((t) => t.name.trim() && t.phone.trim().length >= 10);
  const canSubmit = validTenants.length >= 1 && validTenants.length <= (invite?.maxTenants ?? 1);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !invite) return;
    if (validTenants.length < 1) {
      setSubmitError("At least one tenant is required. Please add name and phone for one tenant.");
      return;
    }
    if (validTenants.length > (invite?.maxTenants ?? 1)) {
      setSubmitError(`You can add at most ${invite?.maxTenants ?? 1} tenant(s).`);
      return;
    }
    if (createAccount && (!accountEmail.trim() || accountPassword.length < 8)) {
      setSubmitError("To create an account, enter email and a password of at least 8 characters.");
      return;
    }
    if (createAccount && accountEmail.trim() && !isValidEmail(accountEmail)) {
      setSubmitError("Please enter a valid email address for the account.");
      return;
    }
    const invalidTenantEmail = validTenants.find((t) => t.email.trim() && !isValidEmail(t.email));
    if (invalidTenantEmail) {
      setSubmitError("Please enter a valid email address for each tenant, or leave email blank.");
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (createAccount && accountEmail.trim() && accountPassword.length >= 8) {
        await api.post(`/join/${token}/create-tenant-account`, {
          email: accountEmail.trim(),
          password: accountPassword,
          name: validTenants[0]?.name.trim() || accountEmail.trim().split("@")[0],
        });
      }
      await api.post(`/join/${token}/signup`, {
        tenants: validTenants.map((t) => ({
          name: t.name.trim(),
          phone: t.phone.trim(),
          email: t.email.trim() || undefined,
        })),
      });
      setSubmitted(true);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setSubmitError(message);
      if (message.includes("already been used")) setAlreadySubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-200 border-t-primary-600" />
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
        <Link to="/" className="inline-block mb-6">
          <Logo size="lg" />
        </Link>
        <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 mb-4">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
          </div>
          <Typography variant="h2">Already submitted</Typography>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            This link has already been used to submit tenant details. If you need to make changes, please ask the owner to share a new link with you.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            Go to home
          </Link>
        </div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
        <Link to="/" className="inline-block mb-6">
          <Logo size="lg" />
        </Link>
        <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 text-center">
          <p className="text-slate-700 dark:text-slate-200 font-medium">{error ?? "Invalid link"}</p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Ask the property owner for a new tenant signup link.
          </p>
          <Link
            to="/"
            className="mt-4 inline-block text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            Go to home
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
        <Link to="/" className="inline-block mb-6">
          <Logo size="lg" />
        </Link>
        <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-4">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <Typography variant="h2">Details submitted</Typography>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            {validTenants.length === 1
              ? "Your details have been submitted. The owner will approve and add you as a tenant shortly."
              : `${validTenants.length} tenants have been submitted. The owner will approve and add them shortly.`}
          </p>
          {createAccount && (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              You can log in with your email and password once the owner approves you.
            </p>
          )}
          <Link
            to={createAccount ? "/login" : "/"}
            className="mt-6 inline-block text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            {createAccount ? "Go to login" : "Go to home"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 px-4 py-8">
      <Link to="/" className="inline-block mb-6">
        <Logo size="lg" />
      </Link>
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 sm:p-8 shadow-sm">
        <Typography variant="h1" className="text-xl font-semibold">Tenant signup</Typography>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          You’re signing up for <strong>{invite.unitAlias}</strong> ({invite.unitNumber}) at{" "}
          <strong>{invite.propertyName}</strong>. Add at least one tenant (required), up to <strong>{invite.maxTenants}</strong> total.
        </p>
        {invite.agreementEndDate && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Agreement end: {new Date(invite.agreementEndDate).toLocaleDateString("en-IN")}
            {invite.moveInDate && ` · Move-in: ${new Date(invite.moveInDate).toLocaleDateString("en-IN")}`}
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {submitError && (
            <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3.5 py-3 text-sm text-red-700 dark:text-red-300">
              {submitError}
            </div>
          )}

          {tenants.map((t, index) => (
            <div key={index} className="rounded-xl border border-slate-200 dark:border-slate-600 p-4 space-y-3">
              <Typography variant="label" as="h3">
                Tenant {index + 1}
                {index === 0 ? (
                  <span className="text-amber-600 dark:text-amber-400 font-normal"> (required)</span>
                ) : invite.maxTenants > 1 ? (
                  <span className="text-slate-400 font-normal"> (optional)</span>
                ) : null}
              </Typography>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Name</label>
                  <input
                    type="text"
                    value={t.name}
                    onChange={(e) => updateTenant(index, "name", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="Full name"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={t.phone}
                    onChange={(e) => updateTenant(index, "phone", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="10-digit number"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Email <span className="text-slate-400">(optional)</span>
                  </label>
                  <input
                    type="email"
                    value={t.email}
                    onChange={(e) => updateTenant(index, "email", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="rounded-xl border border-slate-200 dark:border-slate-600 p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={createAccount}
                onChange={(e) => {
                  setCreateAccount(e.target.checked);
                  if (e.target.checked && validTenants[0]?.email) setAccountEmail(validTenants[0].email);
                }}
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Create an account to log in later</span>
            </label>
            {createAccount && (
              <div className="grid gap-3 sm:grid-cols-2 pt-2">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Email (for login)</label>
                  <input
                    type="email"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <PasswordInput
                    label="Password (min 8 characters)"
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                    placeholder="••••••••"
                    minLength={8}
                    autoComplete="new-password"
                    className="rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600"
          >
            {submitting ? "Submitting…" : validTenants.length > 0 ? `Submit ${validTenants.length} tenant(s)` : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
}
